import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';
import InputFieldV3 from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';
import debounce from 'lodash/debounce';
import _find from 'lodash/find';
import { Form } from 'react-final-form';

import {
  GREATER_THAN_ZERO_OR_BLANK,
  maxIntegerValue,
} from '~/components/common/form/commonValidations';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationContext';
import { extractMinMaxFromSpecialFacets } from '~/pages/couples/explore/util/createVendorSearchFromFilters';
import { extractMinMaxFromSlug } from '~/pages/couples/explore/util/helpers';
import { formatStringToNumber } from '~/util/formatters';
import TextUtils from '~/util/textUtils';

import { getFacetFromPriceRange } from './getFacetFromPriceRange';

import styles from './PriceFacetForm.module.less';

interface PriceFacetFormValues {
  priceMinimum?: string;
  priceMaximum?: string;
}

const maxGreaterThanMin = (value: string | undefined, values: PriceFacetFormValues) => {
  if (values.priceMaximum && value) {
    const minValue = formatStringToNumber(value);
    const maxValue = formatStringToNumber(values.priceMaximum);
    if (maxValue && minValue && minValue >= maxValue) {
      return `Minimum can't be higher than maximum`;
    }
  }
  return undefined;
};

const lessThanOneMillion = (value: string | undefined) => {
  return maxIntegerValue(1_000_000)(formatStringToNumber(value));
};

type PriceFacetFormProps = {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmit?: boolean;
};

export const PriceFacetForm = (props: PriceFacetFormProps) => {
  const { setIsOpen, showSubmit } = props;
  const { selectedFacetOptions } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();
  const submitPrice = (vals: PriceFacetFormValues) => {
    const { priceMinimum, priceMaximum } = vals;
    const priceMinimumValue = priceMinimum ? formatStringToNumber(priceMinimum) : undefined;
    const priceMaximumValue = priceMaximum ? formatStringToNumber(priceMaximum) : undefined;
    const selectedFacet = getFacetFromPriceRange(priceMinimumValue, priceMaximumValue);
    if (selectedFacet) {
      navigateToSrp({
        price: extractMinMaxFromSpecialFacets([selectedFacet]),
      });
      setIsOpen?.(false);
    }
  };

  const previouslySelectedPriceFacet = _find(selectedFacetOptions, (facet) =>
    facet.slug.includes('price')
  );

  const { slug } = previouslySelectedPriceFacet || {};
  const minAndMax = slug ? extractMinMaxFromSlug(slug) : null;
  const { min, max } = minAndMax || {};

  // Use a debounced fn to wait until the user is finished typing before submitting
  const debouncedSubmitPrice = debounce(submitPrice, 200, {
    leading: false,
  });

  return (
    <Form
      onSubmit={submitPrice}
      initialValues={{ priceMinimum: min?.toString(), priceMaximum: max?.toString() }}
      render={({ handleSubmit, form, values }) => {
        return (
          <form key="price-facet-form" onSubmit={handleSubmit} className={styles.formContainer}>
            <div className={cx(styles.inputsWrapper, { [styles.verticalStack]: !showSubmit })}>
              <InputFieldV3
                addOn={InputFieldAddOnType.CASH}
                data-testid="priceMinimum"
                placeholder="Enter a number"
                name="priceMinimum"
                label="Minimum"
                format={(value: string | undefined) => {
                  return TextUtils.formatWithCommas(formatStringToNumber(value));
                }}
                validate={[GREATER_THAN_ZERO_OR_BLANK, lessThanOneMillion, maxGreaterThanMin]}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!showSubmit) {
                    debouncedSubmitPrice({
                      priceMinimum: e.target.value,
                      priceMaximum: values.priceMaximum,
                    });
                  }
                }}
              />
              <InputFieldV3
                addOn={InputFieldAddOnType.CASH}
                placeholder="Enter a number"
                name="priceMaximum"
                label="Maximum"
                data-testid="priceMaximum"
                format={(value: string | undefined) => {
                  return TextUtils.formatWithCommas(formatStringToNumber(value));
                }}
                validate={[GREATER_THAN_ZERO_OR_BLANK, lessThanOneMillion]}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (!showSubmit) {
                    debouncedSubmitPrice({
                      priceMinimum: values.priceMinimum,
                      priceMaximum: e.target.value,
                    });
                  }
                }}
              />
            </div>
            {showSubmit && (
              <div className={styles.ctaWrapper}>
                <LinkV2
                  role="button"
                  type="button"
                  onClick={() => {
                    form.reset({ priceMaximum: undefined, priceMinimum: undefined });
                  }}
                  disabled={!values.priceMaximum && !values.priceMinimum}
                >
                  Reset
                </LinkV2>
                <ButtonV3 disabled={!values.priceMaximum && !values.priceMinimum} type="submit">
                  Done
                </ButtonV3>
              </div>
            )}
          </form>
        );
      }}
    />
  );
};

export default PriceFacetForm;
